import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import PublicBlogSection from '../components/PublicBlogSection';
import EmailPreferencesSection from '../components/EmailPreferencesSection';
import { 
  Gear, 
  User,
  Globe,
  InstagramLogo,
  TiktokLogo,
  LinkedinLogo,
  FacebookLogo,
  CheckCircle,
  Fingerprint,
  Sparkle,
  UsersThree,
  UserPlus,
  Trash,
  Crown,
  EnvelopeSimple
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const industries = ['E-commerce', 'SaaS', 'Service Business', 'Personal Brand', 'Fashion', 'Beauty & Skincare', 'Health & Wellness', 'Tech', 'Food & Beverage', 'Education', 'Finance', 'Real Estate', 'Travel', 'Entertainment'];
const tones = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Educational', 'Bold', 'Friendly', 'Luxurious', 'Playful'];
const goals = ['Increase Sales', 'Build Brand Awareness', 'Generate Leads', 'Grow Social Following', 'Drive Website Traffic', 'Launch Products', 'Build Community'];

export const SettingsPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState({
    brand_name: '',
    industry: '',
    description: '',
    target_audience: '',
    tone_of_voice: '',
    main_goals: [],
    website: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    linkedin: '',
    content_samples: [],
    voice_dna: null,
    brand_colors: { primary: '#c8f060', secondary: '#080808', accent: '#22d3ee' },
    brand_fonts: { heading: 'Instrument Serif', body: 'Inter' },
    logo_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [analyzingVoice, setAnalyzingVoice] = useState(false);
  const [newSample, setNewSample] = useState('');
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');
  const [sharingSettings, setSharingSettings] = useState({
    share_with: 'all',
    shared_features: ['flyer', 'video', 'carousel', 'character', 'voiceover', 'copy', 'seo', 'email', 'meta_ads', 'content'],
    share_with_members: [],
  });
  const [savingSharing, setSavingSharing] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        if (data.brand_profile) {
          setProfile(prev => ({ ...prev, ...data.brand_profile }));
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
    fetchTeam();
    // Handle team invite acceptance from email link
    const inviteId = searchParams.get('accept_invite');
    if (inviteId) {
      acceptInvite(inviteId);
    }
  }, []);

  const acceptInvite = async (inviteId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/team/accept-invite/${inviteId}`);
      toast.success(`Joined ${data.team_name || 'team'} as ${data.role}!`);
      setSearchParams({}, { replace: true });
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
      setSearchParams({}, { replace: true });
    }
  };

  const fetchTeam = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/team`);
      setTeam(data.team);
      if (data.team) {
        try {
          const { data: sharingData } = await axios.get(`${API_URL}/api/team/sharing-settings`);
          if (sharingData.settings) setSharingSettings(sharingData.settings);
        } catch {}
      }
    } catch (e) { /* ignore */ }
  };

  const createTeam = async () => {
    setTeamLoading(true);
    try {
      await axios.post(`${API_URL}/api/team/create`, {});
      await fetchTeam();
    } catch (e) {
      console.error('Failed to create team:', e);
    } finally {
      setTeamLoading(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteStatus('');
    try {
      const { data } = await axios.post(`${API_URL}/api/team/invite`, {
        email: inviteEmail, role: inviteRole
      });
      setInviteStatus(data.status === 'added' ? 'Member added!' : 'Invitation sent!');
      setInviteEmail('');
      await fetchTeam();
    } catch (e) {
      setInviteStatus(e.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API_URL}/api/team/members/${memberId}`);
      await fetchTeam();
    } catch (e) {
      console.error('Failed to remove member:', e);
    }
  };

  const saveSharingSettings = async (updates) => {
    const newSettings = { ...sharingSettings, ...updates };
    setSharingSettings(newSettings);
    setSavingSharing(true);
    try {
      await axios.put(`${API_URL}/api/team/sharing-settings`, newSettings);
      toast.success('Sharing preferences updated');
    } catch (e) {
      toast.error('Failed to save sharing settings');
    } finally {
      setSavingSharing(false);
    }
  };

  const toggleSharedFeature = (feature) => {
    const current = sharingSettings.shared_features || [];
    const updated = current.includes(feature)
      ? current.filter(f => f !== feature)
      : [...current, feature];
    saveSharingSettings({ shared_features: updated });
  };

  const toggleShareWithMember = (memberId) => {
    const current = sharingSettings.share_with_members || [];
    const updated = current.includes(memberId)
      ? current.filter(id => id !== memberId)
      : [...current, memberId];
    saveSharingSettings({ share_with_members: updated });
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const toggleGoal = (goal) => {
    setProfile(prev => ({
      ...prev,
      main_goals: prev.main_goals?.includes(goal)
        ? prev.main_goals.filter(g => g !== goal)
        : [...(prev.main_goals || []), goal]
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, profile);
      setSaved(true);
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const addContentSample = () => {
    if (newSample.trim() && (profile.content_samples?.length || 0) < 5) {
      setProfile(prev => ({ ...prev, content_samples: [...(prev.content_samples || []), newSample.trim()] }));
      setNewSample('');
      setSaved(false);
    }
  };

  const removeSample = (index) => {
    setProfile(prev => ({ ...prev, content_samples: prev.content_samples.filter((_, i) => i !== index) }));
    setSaved(false);
  };

  const analyzeVoice = async () => {
    if (!profile.content_samples?.length) return;
    setAnalyzingVoice(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/brand/voice-analyze`, {
        content_samples: profile.content_samples
      });
      setProfile(prev => ({ ...prev, voice_dna: data.voice_dna }));
    } catch (err) {
      console.error('Voice analysis failed:', err);
    } finally {
      setAnalyzingVoice(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="settings-page" className="space-y-8 max-w-3xl w-full" style={{ overflowX: 'hidden' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <Gear size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Settings</h1>
              <p className="text-zinc-400 text-sm">Manage your brand profile</p>
            </div>
          </div>
        </motion.div>

        {/* Public Blog */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          <PublicBlogSection />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09 }}
        >
          <EmailPreferencesSection />
        </motion.div>

        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6"
        >
          <div className="flex items-center gap-2 mb-6">
            <User size={20} className="text-zinc-400" />
            <h2 className="font-medium font-['Instrument_Serif',serif]">Account</h2>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
              <span className="text-2xl font-medium">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
            </div>
            <div>
              <h3 className="font-medium">{user?.name || 'User'}</h3>
              <p className="text-zinc-400 text-sm">{user?.email || 'user@example.com'}</p>
            </div>
          </div>
        </motion.div>

        {/* Brand Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-6"
        >
          <h2 className="font-medium font-['Instrument_Serif',serif]">Brand Profile</h2>
          <p className="text-sm -mt-4">This information personalizes written for you content for your brand</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Brand Name</label>
              <input
                type="text"
                value={profile.brand_name}
                onChange={(e) => handleChange('brand_name', e.target.value)}
                data-testid="brand-name-input"
                placeholder="Your brand name"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
              />
              <p className="mt-1.5 text-[11px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }} data-testid="brand-name-helper">
                This name appears on all your brand assets and generated content. Enter it exactly as you want it to appear — e.g. <span style={{ color: 'rgba(245,240,232,0.7)' }}>ScalitOS</span>, not <span style={{ color: 'rgba(245,240,232,0.7)' }}>Scalit Operating System</span>.
              </p>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">Industry</label>
              <Select value={profile.industry} onValueChange={(v) => handleChange('industry', v)}>
                <SelectTrigger data-testid="industry-select" className="w-full bg-zinc-900 border-white/10 text-white">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {industries.map((ind) => (
                    <SelectItem key={ind} value={ind} className="text-white hover:bg-white/10">
                      {ind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Brand Description</label>
            <textarea
              value={profile.description}
              onChange={(e) => handleChange('description', e.target.value)}
              data-testid="description-input"
              placeholder="Describe your brand, products/services, and what makes you unique..."
              rows={3}
              className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Target Audience</label>
            <input
              type="text"
              value={profile.target_audience}
              onChange={(e) => handleChange('target_audience', e.target.value)}
              placeholder="e.g., Women 25-45, health-conscious professionals"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tone of Voice</label>
            <Select value={profile.tone_of_voice} onValueChange={(v) => handleChange('tone_of_voice', v)}>
              <SelectTrigger data-testid="tone-select" className="w-full bg-zinc-900 border-white/10 text-white">
                <SelectValue placeholder="Select tone" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {tones.map((tone) => (
                  <SelectItem key={tone} value={tone} className="text-white hover:bg-white/10">
                    {tone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Main Goals (select all that apply)</label>
            <div className="flex flex-wrap gap-2 w-full" style={{ minWidth: 0 }}>
              {goals.map((goal) => (
                <button
                  key={goal}
                  onClick={() => toggleGoal(goal)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    profile.main_goals?.includes(goal)
                      ? 'bg-white text-black'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Brand Visual Identity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5"
          data-testid="brand-identity-section"
        >
          <div className="flex items-center gap-2">
            <Sparkle size={20} className="text-zinc-400" />
            <div>
              <h2 className="font-medium font-['Instrument_Serif',serif]">Brand Visual Identity</h2>
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>These colors are applied to all generated visuals — flyers, carousels, ads, and landing pages.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'primary', label: 'Primary' },
              { key: 'secondary', label: 'Secondary' },
              { key: 'accent', label: 'Accent' },
            ].map(c => (
              <div key={c.key}>
                <label className="block text-sm text-zinc-400 mb-2">{c.label}</label>
                <div className="flex items-center gap-2">
                  <input type="color"
                    value={profile.brand_colors?.[c.key] || '#000000'}
                    onChange={e => handleChange('brand_colors', { ...(profile.brand_colors || {}), [c.key]: e.target.value })}
                    data-testid={`color-${c.key}`}
                    className="w-12 h-12 rounded-lg cursor-pointer bg-transparent border border-white/10" />
                  <input type="text"
                    value={profile.brand_colors?.[c.key] || ''}
                    onChange={e => handleChange('brand_colors', { ...(profile.brand_colors || {}), [c.key]: e.target.value })}
                    placeholder="#hex"
                    className="flex-1 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Heading Font</label>
              <input type="text"
                value={profile.brand_fonts?.heading || ''}
                onChange={e => handleChange('brand_fonts', { ...(profile.brand_fonts || {}), heading: e.target.value })}
                placeholder="e.g. Instrument Serif"
                data-testid="font-heading"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Body Font</label>
              <input type="text"
                value={profile.brand_fonts?.body || ''}
                onChange={e => handleChange('brand_fonts', { ...(profile.brand_fonts || {}), body: e.target.value })}
                placeholder="e.g. Inter"
                data-testid="font-body"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
            </div>
          </div>

          {/* Logo URL — optional watermark */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">
              Logo URL <span className="text-[10px] text-zinc-500">(optional — adds a small watermark to all generated visuals)</span>
            </label>
            <div className="flex items-center gap-3">
              <input type="text"
                value={profile.logo_url || ''}
                onChange={e => handleChange('logo_url', e.target.value)}
                placeholder="https://your-logo.com/logo.png"
                data-testid="logo-url-input"
                className="flex-1 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
              {profile.logo_url && (
                <>
                  <img src={profile.logo_url} alt="logo preview" data-testid="logo-preview"
                    className="w-12 h-12 rounded-lg object-contain bg-white/5 border border-white/10 p-1"
                    onError={e => { e.currentTarget.style.display = 'none'; }} />
                  <button onClick={() => handleChange('logo_url', '')} data-testid="logo-remove-btn"
                    className="px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,68,68,0.06)', color: '#ff4444', border: '1px solid rgba(255,68,68,0.15)' }}>
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-xl p-5 border border-white/10" data-testid="brand-preview"
            style={{
              background: profile.brand_colors?.secondary || '#080808',
              fontFamily: profile.brand_fonts?.body || 'Inter',
            }}>
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: profile.brand_colors?.accent || '#22d3ee' }}>Brand preview</p>
            <h3 className="text-2xl mb-1" style={{ color: profile.brand_colors?.primary || '#c8f060', fontFamily: profile.brand_fonts?.heading || 'Instrument Serif' }}>
              {profile.brand_name || 'Your Brand'}
            </h3>
            <p className="text-sm" style={{ color: '#f5f0e8' }}>
              {profile.description || 'Your brand description appears here, styled with your fonts and colors.'}
            </p>
          </div>
        </motion.div>


        {/* Brand Voice DNA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5"
        >
          <div className="flex items-center gap-2">
            <Fingerprint size={20} className="text-zinc-400" />
            <div>
              <h2 className="font-medium font-['Instrument_Serif',serif]">Brand Voice DNA</h2>
              <p className="text-sm">Paste your best content so ScalitOS writes in your voice.</p>
            </div>
          </div>
          
          {/* Content Samples */}
          <div className="space-y-3">
            {profile.content_samples?.map((sample, i) => (
              <div key={i} className="bg-zinc-800/50 rounded-xl p-3 flex items-start gap-3">
                <p className="text-sm text-zinc-300 flex-1 line-clamp-3">{sample}</p>
                <button onClick={() => removeSample(i)} className="text-zinc-500 hover:text-red-400 transition-colors text-xs shrink-0">Remove</button>
              </div>
            ))}
            
            {(profile.content_samples?.length || 0) < 5 && (
              <div>
                <textarea
                  value={newSample}
                  onChange={(e) => setNewSample(e.target.value)}
                  data-testid="content-sample-input"
                  placeholder="Paste a caption, email, or any content that represents your brand voice..."
                  rows={3}
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 text-sm resize-none mb-2"
                />
                <button onClick={addContentSample} disabled={!newSample.trim()} data-testid="add-sample-btn"
                  className="text-sm text-zinc-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-30">
                  Add Sample ({profile.content_samples?.length || 0}/5)
                </button>
              </div>
            )}
          </div>

          {/* Analyze Button */}
          {profile.content_samples?.length >= 1 && (
            <button onClick={analyzeVoice} disabled={analyzingVoice} data-testid="analyze-voice-btn"
              className="bg-white text-black hover:bg-zinc-200 transition-all rounded-full font-medium px-6 py-2.5 text-sm disabled:opacity-50 flex items-center gap-2">
              {analyzingVoice ? (<><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" /> Analyzing Voice...</>) : (<><Sparkle size={16} /> Analyze My Voice DNA</>)}
            </button>
          )}

          {/* Voice DNA Results */}
          {profile.voice_dna && (
            <div className="bg-zinc-800/30 border border-white/5 rounded-xl p-5 space-y-3">
              <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Your Voice DNA</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: 'Personality', value: profile.voice_dna.personality },
                  { label: 'Emotional Tone', value: profile.voice_dna.emotional_tone },
                  { label: 'Communication Style', value: profile.voice_dna.communication_style },
                  { label: 'Brand Positioning', value: profile.voice_dna.brand_positioning },
                  { label: 'Vocabulary', value: profile.voice_dna.vocabulary_patterns },
                  { label: 'Sentence Style', value: profile.voice_dna.sentence_structure }
                ].map(({ label, value }) => value && (
                  <div key={label} className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500 mb-1">{label}</p>
                    <p className="text-sm text-zinc-200">{value}</p>
                  </div>
                ))}
              </div>
              {profile.voice_dna.summary && (
                <div className="mt-2 p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Summary</p>
                  <p className="text-sm text-zinc-300">{profile.voice_dna.summary}</p>
                </div>
              )}
              {profile.voice_dna.unique_traits?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {profile.voice_dna.unique_traits.map((trait, i) => (
                    <span key={i} className="text-xs bg-white/10 text-zinc-300 px-2 py-1 rounded-full">{trait}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Social Profiles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5"
        >
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-zinc-400" />
            <h2 className="font-medium font-['Instrument_Serif',serif]">Online Presence</h2>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Website</label>
            <input
              type="url"
              value={profile.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://yourbrand.com"
              className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                <InstagramLogo size={16} className="inline mr-2" />
                Instagram
              </label>
              <input
                type="text"
                value={profile.instagram}
                onChange={(e) => handleChange('instagram', e.target.value)}
                placeholder="@yourbrand"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                <TiktokLogo size={16} className="inline mr-2" />
                TikTok
              </label>
              <input
                type="text"
                value={profile.tiktok}
                onChange={(e) => handleChange('tiktok', e.target.value)}
                placeholder="@yourbrand"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                <FacebookLogo size={16} className="inline mr-2" />
                Facebook
              </label>
              <input
                type="text"
                value={profile.facebook}
                onChange={(e) => handleChange('facebook', e.target.value)}
                placeholder="facebook.com/yourbrand"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                <LinkedinLogo size={16} className="inline mr-2" />
                LinkedIn
              </label>
              <input
                type="text"
                value={profile.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="linkedin.com/company/yourbrand"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
              />
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
            className="bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              'Saving...'
            ) : saved ? (
              <>
                <CheckCircle size={18} weight="fill" />
                Saved
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </motion.div>

        {/* Team Collaboration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5"
        >
          <div className="flex items-center gap-2">
            <UsersThree size={20} className="text-zinc-400" />
            <div>
              <h2 className="font-medium font-['Instrument_Serif',serif]">Team Collaboration</h2>
              <p className="text-sm">Invite team members to collaborate on your brand</p>
            </div>
          </div>

          {!team ? (
            <div className="text-center py-6">
              <p className="text-zinc-400 text-sm mb-4">Create a team to start collaborating with others</p>
              <button onClick={createTeam} disabled={teamLoading} data-testid="create-team-btn"
                className="bg-white text-black hover:bg-zinc-200 transition-all rounded-full font-medium px-6 py-2.5 text-sm disabled:opacity-50 flex items-center gap-2 mx-auto">
                {teamLoading ? 'Creating...' : <><UsersThree size={16} /> Create Team</>}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-400">Team:</span>
                <span className="font-medium">{team.name}</span>
              </div>

              {/* Members */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Members ({team.members?.length || 0})</p>
                {team.members?.map((member, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium">
                        {(member.name || member.email)?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm">{member.name || member.email}</p>
                        <p className="text-xs text-zinc-500">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        member.role === 'owner' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                        member.role === 'admin' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                        'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                      }`}>
                        {member.role === 'owner' && <Crown size={10} className="inline mr-1" />}
                        {member.role}
                      </span>
                      {member.role !== 'owner' && team.owner_id === user?.id && (
                        <button onClick={() => removeMember(member.user_id)} data-testid={`remove-member-${i}`}
                          className="text-zinc-500 hover:text-red-400 transition-colors">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Invite */}
              <div className="bg-zinc-800/30 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium flex items-center gap-2"><UserPlus size={16} /> Invite Member</p>
                <div className="flex gap-2">
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    data-testid="invite-email-input" placeholder="colleague@email.com"
                    className="flex-1 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-white/30 text-sm" />
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                    className="bg-zinc-900 border border-white/10 rounded-xl text-white px-3 py-2.5 text-sm">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()} data-testid="invite-member-btn"
                    className="bg-white text-black hover:bg-zinc-200 rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-50 flex items-center gap-1.5">
                    {inviting ? 'Inviting...' : <><EnvelopeSimple size={14} /> Invite</>}
                  </button>
                </div>
                {inviteStatus && (
                  <p className={`text-xs ${inviteStatus.includes('fail') || inviteStatus.includes('Already') ? 'text-red-400' : 'text-green-400'}`}>
                    {inviteStatus}
                  </p>
                )}
              </div>

              {/* Sharing Preferences */}
              <div className="bg-zinc-800/30 rounded-xl p-4 space-y-4" data-testid="sharing-preferences">
                <p className="text-sm font-medium">Sharing Preferences</p>
                <p className="text-xs text-zinc-500">Choose what content you share with your team</p>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Share with</p>
                  <div className="flex gap-2 flex-wrap">
                    {[{ id: 'all', label: 'All Members' }, { id: 'selected', label: 'Selected Members' }, { id: 'none', label: 'No One' }].map(opt => (
                      <button key={opt.id}
                        onClick={() => saveSharingSettings({ share_with: opt.id })}
                        data-testid={`share-with-${opt.id}`}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        style={{
                          background: sharingSettings.share_with === opt.id ? 'rgba(200,240,96,0.1)' : 'transparent',
                          border: `1px solid ${sharingSettings.share_with === opt.id ? 'rgba(200,240,96,0.3)' : 'rgba(255,255,255,0.08)'}`,
                          color: sharingSettings.share_with === opt.id ? '#c8f060' : 'rgba(245,240,232,0.4)',
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {sharingSettings.share_with === 'selected' && team?.members?.length > 1 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Select Members</p>
                    <div className="space-y-1">
                      {team.members.filter(m => m.user_id !== user?.id).map((member, i) => (
                        <label key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 cursor-pointer text-xs">
                          <input type="checkbox" 
                            checked={(sharingSettings.share_with_members || []).includes(member.user_id)}
                            onChange={() => toggleShareWithMember(member.user_id)}
                            className="rounded border-white/20" />
                          <span>{member.name || member.email}</span>
                          <span className="text-zinc-500 ml-auto">{member.role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Shared Features</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {[
                      { id: 'flyer', label: 'Flyers' }, { id: 'video', label: 'Videos' },
                      { id: 'carousel', label: 'Carousels' }, { id: 'character', label: 'Characters' },
                      { id: 'voiceover', label: 'Voiceovers' }, { id: 'copy', label: 'Copy' },
                      { id: 'seo', label: 'SEO' }, { id: 'email', label: 'Emails' },
                      { id: 'meta_ads', label: 'Meta Ads' }, { id: 'content', label: 'Content' },
                    ].map(feat => (
                      <label key={feat.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 cursor-pointer text-xs"
                        data-testid={`share-feature-${feat.id}`}>
                        <input type="checkbox"
                          checked={(sharingSettings.shared_features || []).includes(feat.id)}
                          onChange={() => toggleSharedFeature(feat.id)}
                          className="rounded border-white/20" />
                        <span>{feat.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
